"use client";
import React, { useMemo, useState } from "react";
import { BadgePercent, Edit3Icon, Plus, Trash2, X } from "lucide-react";
import { createPortal } from "react-dom";
import { useExpenseVouchers } from "../context/ExpenseVoucherContext";
import { useExpenses } from "../context/ExpenseContext";
/* ================= MAIN PAGE ================= */

export default function ExpenseVoucherPage() {
  const {
    vouchers,
    addVoucher,
    updateVoucher,
    toggleStatus,
    deleteVoucher,
  } = useExpenseVouchers();
  const { expenses } = useExpenses();

  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [expenseId, setExpenseId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  /* ---------- FILTER & SORT ---------- */
  const filtered = useMemo(() => {
    let data = vouchers.filter((d) =>
      d.expense_name.toLowerCase().includes(search.toLowerCase())
    );

    if (sortBy === "az")
      data.sort((a, b) => a.expense_name.localeCompare(b.expense_name));
    if (sortBy === "za")
      data.sort((a, b) => b.expense_name.localeCompare(a.expense_name));

    return data;
  }, [vouchers, search, sortBy]);

  /* ---------- SAVE ---------- */
const save = async () => {
  if (!expenseId || !amount) return;

  const payload = {
    expense_id: Number(expenseId), // 🔥 FIX HERE
    amount: Number(amount),
    note,
  };

  if (editing) {
    await updateVoucher(editing.id, payload);
  } else {
    await addVoucher(payload);
  }

  setModalOpen(false);
  setEditing(null);
  resetForm();
};

  const resetForm = () => {
    setExpenseId("");
    setAmount("");
    setNote("");
  };

  const removeHandler = (id) => {
    if (window.confirm("Are you sure you want to delete this voucher?")) {
      deleteVoucher(id);
    }
  };

  /* ================= UI ================= */

  return (
    <div className="p-4 md:p-6 space-y-6">

      {/* Header (same as Expenses) */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">

        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-teal-700 rounded-xl flex items-center justify-center shadow-sm">
            <BadgePercent className="w-6 h-6 text-white" />
          </div>

          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-teal-800">
              Expense Vouchers
            </h1>
            <p className="text-gray-600">
              Manage your voucher entries
            </p>
          </div>
        </div>

        <button
          onClick={() => {
            setModalOpen(true);
            setEditing(null);
            resetForm();
          }}
          className="w-full sm:w-auto flex items-center justify-center gap-2 bg-teal-600 text-white px-5 py-2.5 rounded-xl hover:bg-teal-700 transition"
        >
          <Plus className="w-5 h-5" />
          Add Voucher
        </button>
      </div>

      {/* Stats (same style as Expenses) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

        <Stat label="Total" value={vouchers.length} />

        <Stat
          label="Active"
          value={vouchers.filter((d) => d.status === "active").length}
          color="text-green-600"
        />

        <Stat
          label="Inactive"
          value={vouchers.filter((d) => d.status === "inactive").length}
          color="text-red-600"
        />
      </div>

      {/* Search & Sort */}
      <div className="flex flex-col md:flex-row gap-4 bg-orange-50 border border-orange-200 rounded-xl p-4">

        <input
          placeholder="Search voucher..."
          className="px-4 py-2 border rounded-xl w-full md:w-80"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="px-4 py-2 border rounded-xl w-full md:w-52"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="">Sort By</option>
          <option value="az">Name A → Z</option>
          <option value="za">Name Z → A</option>
        </select>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto bg-white rounded-xl shadow-sm border">

        <table className="min-w-full text-sm text-left">

          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-6 py-3">ID</th>
              <th className="px-6 py-3">Expense</th>
              <th className="px-6 py-3">Amount</th>
              <th className="px-6 py-3">Note</th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>

          <tbody>
            {filtered.map((item) => (
              <tr key={item.id} className="border-t hover:bg-gray-50">

                <td className="px-6 py-4">{item.id}</td>
                <td className="px-6 py-4 font-medium">{item.expense_name}</td>
                <td className="px-6 py-4">₹ {item.amount}</td>
                <td className="px-6 py-4 text-gray-600">{item.note}</td>

                <td className="px-6 py-4">
  <button
    onClick={() => toggleStatus(item.id, item.status)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full ${
      item.status === "active" ? "bg-green-600" : "bg-gray-300"
    }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white ${
        item.status === "active"
          ? "translate-x-6"
          : "translate-x-1"
      }`}
    />
  </button>
</td>

                <td className="px-6 py-4">
                  <div className="flex justify-end">

                    <Actions
                      item={item}
                      onEdit={() => {
                        setEditing(item);
                        setExpenseId(item.expense_id);
                        setAmount(item.amount);
                        setNote(item.note);
                        setModalOpen(true);
                      }}
                      onDelete={removeHandler}
                    />

                  </div>
                </td>

              </tr>
            ))}
          </tbody>

        </table>
      </div>

      {/* Mobile Cards (same as Expenses) */}
      <div className="md:hidden space-y-4">

        {filtered.map((item) => (
          <div key={item.id} className="bg-white rounded-xl shadow p-4 space-y-2">

            <div className="flex justify-between">

              <div>
                <p className="text-xs text-gray-500">ID</p>
                <p>{item.id}</p>
              </div>

              <Actions
                item={item}
                onEdit={() => {
                  setEditing(item);
                  setExpenseId(item.expense_id);                  
                  setAmount(item.amount);
                  setNote(item.note);
                  setModalOpen(true);
                }}
                onDelete={removeHandler}
              />

            </div>

            <div>
              <p className="text-xs text-gray-500">Expense</p>
              <p className="font-medium">{item.expense_name}</p>
            </div>

            <div>
              <p className="text-xs text-gray-500">Amount</p>
              <p>₹ {item.amount}</p>
            </div>

            <div>
              <p className="text-xs text-gray-500">Note</p>
              <p>{item.note}</p>
            </div>

            <button
  onClick={() => toggleStatus(item.id, item.status)}
  className={`relative inline-flex h-6 w-11 items-center rounded-full ${
    item.status === "active" ? "bg-green-600" : "bg-gray-300"
  }`}
>
  <span
    className={`inline-block h-4 w-4 transform rounded-full bg-white ${
      item.status === "active"
        ? "translate-x-6"
        : "translate-x-1"
    }`}
  />
</button>

          </div>
        ))}

      </div>

      {/* Modal (same style as Expenses) */}
      {modalOpen &&
        createPortal(
          <>
            <div
              className="fixed inset-0 bg-black/70 backdrop-blur-md z-[1000]"
              onClick={() => setModalOpen(false)}
            />

            <div className="fixed inset-0 z-[1010] flex items-start justify-end">

              <div
                className="w-full max-w-md min-h-screen bg-gradient-to-br from-orange-50 to-orange-200 rounded-2xl shadow-2xl p-6 relative"
                onClick={(e) => e.stopPropagation()}
              >

                <button
                  onClick={() => setModalOpen(false)}
                  className="absolute top-4 right-4 w-8 h-8 bg-black text-white rounded-full flex items-center justify-center"
                >
                  <X size={18} />
                </button>

                <h2 className="text-2xl font-bold text-teal-800 text-center mb-6">
                  {editing ? "Edit Voucher" : "Add Voucher"}
                </h2>

                <select
  value={expenseId}
  onChange={(e) => setExpenseId(e.target.value)}
  className="w-full px-4 py-3 border rounded-xl mb-4"
>
  <option value="">Select Expense</option>

  {expenses.map((exp) => (
    <option key={exp.id} value={exp.id}>
      {exp.name}
    </option>
  ))}
</select>

                <input
  value={amount}
  onChange={(e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) setAmount(value);
  }}
  onPaste={(e) => {
    const paste = e.clipboardData.getData("text");
    if (!/^\d+$/.test(paste)) e.preventDefault();
  }}
  placeholder="Amount"
  className="w-full px-4 py-3 border rounded-xl mb-4"
/>

                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Note"
                  className="w-full px-4 py-3 border rounded-xl mb-6"
                />

                <div className="flex gap-4">

                  <button
                    onClick={() => setModalOpen(false)}
                    className="flex-1 px-4 py-3 bg-white border rounded-xl"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={save}
                    className="flex-1 px-4 py-3 bg-teal-600 text-white rounded-xl"
                  >
                    Save
                  </button>

                </div>

              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
}

/* ================= COMPONENTS ================= */

const Stat = ({ label, value, color = "text-gray-900" }) => (
  <div className="bg-white border border-orange-100 rounded-xl p-4">
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
  </div>
);

const Actions = ({ item, onEdit, onDelete }) => (
  <div className="flex items-center gap-3 justify-end">

    <button onClick={onEdit} className="text-teal-600">
      <Edit3Icon size={16} />
    </button>

    <button onClick={() => onDelete(item.id)} className="text-red-500">
      <Trash2 size={16} />
    </button>

  </div>
);